// The Reconcile Wells model: which records are included, which distinct well names
// survive, and which fold into them. Kept as a pure module (no React, no fetching) so
// the interaction rules are testable on their own and wiring real endpoints later is a
// change of data source rather than of logic.
//
// It deliberately mirrors the shape of the entity reconcile engine
// (pages/entities/show/reconcile/entity-consolidation.ts) — a reducer plus selectors —
// but carries wells fields only: no entity types, addresses, aliases or canonize.

export type DupReasonKind = 'name-surface' | 'surface-bottom';

/** Why two records are suspected duplicates. `detail` is the cell's tooltip. */
export interface DupReason {
  kind: DupReasonKind;
  label: string;
  detail: string;
}

/** A directional survey attached to a record — part of what makes it "complete". */
export interface WellSurveySummary {
  surveyId: string;
  source: string;
  stations: number;
  mdMinFt: number;
  mdMaxFt: number;
}

export interface WellCandidate {
  /** Row identity: the UWI10. */
  rowId: string;
  uwi: string;
  /**
   * The bore id (UWI14) of the producing bore, shown beside the UWI10 so the operator
   * can tell a surviving bore from a duplicate. Falls back to the permit-header UWI14 when
   * the well has no real bore (stick-only or none); null only when it has no permit either.
   */
  uwi14: string | null;
  /**
   * Whether the well has a real wellbore geometry (surveyed/pre-drill/estimated bore), as
   * opposed to only an auto-stick or none. Drives the sheet's "Has Geometry" column.
   */
  hasGeometry: boolean;
  wellName: string;
  operator: string;
  wellStatus: string;
  measuredDepth: number;
  tvd: number | null;
  perfLateral: number | null;
  spudDate: string | null;
  firstProductionDate: string | null;
  hasSurfaceHole: boolean;
  hasBottomHole: boolean;
  survey: WellSurveySummary | null;
  /** Empty for the viewed well — it is the thing being matched against. */
  reasons: DupReason[];
  /** The viewed well that opened the sheet. Its Source column reads "Viewed Well". */
  isAnchor: boolean;
  /**
   * The record the detector nominated to survive this cluster. Absent when the pool
   * came from somewhere that carries no such signal, which is the only case where the
   * completeness score below decides on its own.
   */
  isSuggestedSurvivor?: boolean;
  /** Pulled in by hand via "Add wells to reconcile": Source reads "Added Well", reason "Added by you". */
  isAdded?: boolean;
}

export interface WellConsolidationState {
  /** The record pool, viewed well first. Immutable for the life of the sheet. */
  pool: WellCandidate[];
  checkedIds: Set<string>;
  /** Insertion order is the numbering: index 0 is Survivor 1. */
  survivorKeys: string[];
  dependents: { key: string; survivorKey: string }[];
  /** Per-name pinned surviving record. A pin is the user's choice and is sovereign. */
  pins: Record<string, string | null>;
  /** Unactioned names the user hid; restorable, never discarded. */
  hiddenKeys: Set<string>;
}

export type WellConsolidationAction =
  | { type: 'check'; id: string }
  | { type: 'uncheck'; id: string }
  | { type: 'checkAll' }
  | { type: 'uncheckAll' }
  | { type: 'toggleSurvivor'; key: string }
  | { type: 'includeAll'; key: string }
  | { type: 'removeName'; key: string }
  | { type: 'restoreName'; key: string }
  | { type: 'setRepresentative'; key: string; id: string }
  | { type: 'drag'; key: string; survivorKey: string }
  | { type: 'addWells'; candidates: WellCandidate[] };

export interface NameGroup {
  key: string;
  name: string;
  members: WellCandidate[];
}

export type NameStatus = 'survivor' | 'dependent' | 'unactioned';

/** What a reconcile commits: one surviving record and the records it retires. */
export interface WellReconcileAssignment {
  survivorId: string;
  retiredIds: string[];
}

/**
 * The distinct-well-name grouping key. Case-folded with runs of whitespace collapsed;
 * everything else stays significant, so "… 8E 205H ST02" is its own name rather than
 * folding into "… 8E 205H" on its own.
 * @param {string} name The well name.
 * @return {string} The grouping key.
 */
export const nameKeyOf = (name: string): string =>
  name.trim().toLowerCase().replace(/\s+/g, ' ');

// ─── Selectors ──────────────────────────────────────────────────────────────

/**
 * The pool grouped by distinct well name, in first-appearance order.
 * @param {WellConsolidationState} state The model.
 * @return {NameGroup[]} One group per distinct name.
 */
export const groupsOf = (state: WellConsolidationState): NameGroup[] => {
  const byKey = new Map<string, NameGroup>();
  state.pool.forEach((well) => {
    const key = nameKeyOf(well.wellName);
    const group = byKey.get(key);
    if (group) group.members.push(well);
    else byKey.set(key, { key, name: well.wellName, members: [well] });
  });
  return [...byKey.values()];
};

/**
 * One distinct name's group.
 * @param {WellConsolidationState} state The model.
 * @param {string} key The name key.
 * @return {NameGroup | undefined} The group, when the name is in the pool.
 */
export const groupOf = (
  state: WellConsolidationState,
  key: string
): NameGroup | undefined => groupsOf(state).find((group) => group.key === key);

/**
 * A name's role in the structure being built.
 * @param {WellConsolidationState} state The model.
 * @param {string} key The name key.
 * @return {NameStatus} Survivor, dependent, or unactioned.
 */
export const statusOf = (
  state: WellConsolidationState,
  key: string
): NameStatus => {
  if (state.survivorKeys.includes(key)) return 'survivor';
  if (state.dependents.some((dep) => dep.key === key)) return 'dependent';
  return 'unactioned';
};

/**
 * The checked members of a name — what "X of Y included" counts.
 * @param {WellConsolidationState} state The model.
 * @param {string} key The name key.
 * @return {WellCandidate[]} The included records.
 */
export const includedOf = (
  state: WellConsolidationState,
  key: string
): WellCandidate[] =>
  (groupOf(state, key)?.members ?? []).filter((member) =>
    state.checkedIds.has(member.rowId)
  );

/**
 * Data completeness — one point per populated identity/production fact, and two for a
 * directional survey, which is the fact most worth carrying onto the surviving record.
 * @param {WellCandidate} well The record.
 * @return {number} Its score.
 */
export const wellCompletenessScore = (well: WellCandidate): number => {
  let score = 0;
  if (well.measuredDepth > 0) score += 1;
  if (well.tvd !== null) score += 1;
  if (well.perfLateral !== null) score += 1;
  if (well.spudDate) score += 1;
  if (well.firstProductionDate) score += 1;
  if (well.hasSurfaceHole) score += 1;
  if (well.hasBottomHole) score += 1;
  if (well.survey) score += 2;
  return score;
};

/**
 * Row id order, so a completeness tie resolves the same way every time.
 * @param {WellCandidate} a The first record.
 * @param {WellCandidate} b The second record.
 * @return {number} Negative when `a` sorts first, as Array#sort expects.
 */
const compareRowIds = (a: WellCandidate, b: WellCandidate) =>
  a.rowId.localeCompare(b.rowId, undefined, { numeric: true });

/**
 * The default surviving record.
 *
 * The detector's own nomination wins where there is one — it resolved the cluster and
 * the client has no better information than the rule that built it. Only where no
 * record is nominated does the completeness score decide: most complete first, ties
 * broken by lowest row id.
 * @param {WellCandidate[]} members The records to elect among.
 * @return {WellCandidate | null} The winner, or null when there are none.
 */
export const electWellSurvivor = (
  members: WellCandidate[]
): WellCandidate | null => {
  if (members.length === 0) return null;

  const nominated = members.filter((member) => member.isSuggestedSurvivor);
  if (nominated.length === 1) return nominated[0];

  return members.reduce((best, member) => {
    const bestScore = wellCompletenessScore(best);
    const memberScore = wellCompletenessScore(member);
    if (memberScore !== bestScore)
      return memberScore > bestScore ? member : best;
    return compareRowIds(member, best) < 0 ? member : best;
  });
};

/**
 * Which record currently stands for a name. A pin is the user's choice and wins
 * outright; otherwise the included members elect one, with the full membership
 * standing in while nothing is included yet.
 * @param {WellCandidate[]} members The name's records.
 * @param {ReadonlySet<string>} includedIds The checked row ids.
 * @param {string | null | undefined} pinnedId The pinned row id, when set.
 * @return {WellCandidate | null} The surviving record.
 */
export const resolveSurvivingWell = (
  members: WellCandidate[],
  includedIds: ReadonlySet<string>,
  pinnedId?: string | null
): WellCandidate | null => {
  if (members.length === 0) return null;
  if (pinnedId) {
    const pinned = members.find((member) => member.rowId === pinnedId);
    if (pinned) return pinned;
  }
  const included = members.filter((member) => includedIds.has(member.rowId));
  return electWellSurvivor(included.length > 0 ? included : members);
};

/**
 * The surviving record for one survivor name, under the current pins and selection.
 * @param {WellConsolidationState} state The model.
 * @param {string} key The survivor's name key.
 * @return {WellCandidate | null} The surviving record.
 */
export const survivingWellOf = (
  state: WellConsolidationState,
  key: string
): WellCandidate | null => {
  const group = groupOf(state, key);
  if (!group) return null;
  return resolveSurvivingWell(group.members, state.checkedIds, state.pins[key]);
};

/**
 * Every record that ends up under a survivor: its own included members plus those of
 * each name folded beneath it. This is the radio pool on the Survivor card.
 * @param {WellConsolidationState} state The model.
 * @param {string} key The survivor's name key.
 * @return {WellCandidate[]} The cluster's included records.
 */
export const clusterOf = (
  state: WellConsolidationState,
  key: string
): WellCandidate[] => [
  ...includedOf(state, key),
  ...state.dependents
    .filter((dep) => dep.survivorKey === key)
    .flatMap((dep) => includedOf(state, dep.key)),
];

/**
 * What the sheet would commit. Only survivors that actually retire another record
 * produce an assignment, so a lone viewed well commits nothing.
 * @param {WellConsolidationState} state The model.
 * @return {WellReconcileAssignment[]} The survivor/retired pairs.
 */
export const assignments = (
  state: WellConsolidationState
): WellReconcileAssignment[] =>
  state.survivorKeys.flatMap((key) => {
    const survivor = survivingWellOf(state, key);
    if (!survivor) return [];
    const retiredIds = clusterOf(state, key)
      .filter((member) => member.rowId !== survivor.rowId)
      .map((member) => member.rowId);
    if (retiredIds.length === 0) return [];
    return [{ survivorId: survivor.rowId, retiredIds }];
  });

/**
 * The success toast's sentences, one per retired record — "X (UWI a) is now a past
 * version of Y (UWI b)." — as the mock words them.
 * @param {WellReconcileAssignment[]} committed What was reconciled.
 * @param {WellCandidate[]} pool The records the sheet worked, catalog-added ones included.
 * @return {string[]} One sentence per retired record.
 */
export const reconcileSummary = (
  committed: WellReconcileAssignment[],
  pool: WellCandidate[]
): string[] => {
  const byRowId = new Map(pool.map((well) => [well.rowId, well]));
  /**
   * @param {string} rowId A reconciled record's row id.
   * @return {string} "NAME (UWI x)", or the bare UWI when the pool doesn't hold it.
   */
  const label = (rowId: string): string => {
    const well = byRowId.get(rowId);
    return well ? `${well.wellName} (UWI ${well.uwi})` : `UWI ${rowId}`;
  };
  return committed.flatMap(({ survivorId, retiredIds }) =>
    retiredIds.map(
      (id) => `${label(id)} is now a past version of ${label(survivorId)}.`
    )
  );
};

/**
 * Whether the sheet has a structure worth committing — what gates the Reconcile button.
 * @param {WellConsolidationState} state The model.
 * @return {boolean} True once at least one survivor would retire a record.
 */
export const canReconcile = (state: WellConsolidationState): boolean =>
  assignments(state).length > 0;

// ─── Reducer ────────────────────────────────────────────────────────────────

/** A mutable working copy, so one action can settle several slices coherently. */
interface Draft {
  checkedIds: Set<string>;
  survivorKeys: string[];
  dependents: { key: string; survivorKey: string }[];
  pins: Record<string, string | null>;
  hiddenKeys: Set<string>;
}

/**
 * Open a working copy of the mutable slices.
 * @param {WellConsolidationState} state The model to copy.
 * @return {Draft} The working copy.
 */
const draftOf = (state: WellConsolidationState): Draft => ({
  checkedIds: new Set(state.checkedIds),
  survivorKeys: [...state.survivorKeys],
  dependents: state.dependents.map((dep) => ({ ...dep })),
  pins: { ...state.pins },
  hiddenKeys: new Set(state.hiddenKeys),
});

/**
 * Fold a settled working copy back into the model.
 * @param {WellConsolidationState} state The model the draft came from.
 * @param {Draft} draft The settled working copy.
 * @return {WellConsolidationState} The next model.
 */
const commit = (
  state: WellConsolidationState,
  draft: Draft
): WellConsolidationState => ({
  ...state,
  ...draft,
});

/**
 * Revert a survivor and everything folded beneath it, unchecking their records.
 * @param {WellConsolidationState} state The model, read for group membership.
 * @param {Draft} draft The working copy to mutate.
 * @param {string} key The survivor's name key.
 * @return {void} Nothing; the draft is mutated in place.
 */
const tearDownSurvivor = (
  state: WellConsolidationState,
  draft: Draft,
  key: string
) => {
  const index = draft.survivorKeys.indexOf(key);
  if (index >= 0) draft.survivorKeys.splice(index, 1);
  draft.pins[key] = null;
  groupOf(state, key)?.members.forEach((member) =>
    draft.checkedIds.delete(member.rowId)
  );
  draft.dependents
    .filter((dep) => dep.survivorKey === key)
    .forEach((dep) => {
      groupOf(state, dep.key)?.members.forEach((member) =>
        draft.checkedIds.delete(member.rowId)
      );
    });
  draft.dependents = draft.dependents.filter((dep) => dep.survivorKey !== key);
};

/**
 * Include every record of a name — the whole-name sweep that electing or folding a name
 * performs, so a name never ends up structured with none of its records included.
 * @param {WellConsolidationState} state The model, read for group membership.
 * @param {Draft} draft The working copy to mutate.
 * @param {string} key The name key.
 * @return {void} Nothing; the draft is mutated in place.
 */
const sweepChecked = (
  state: WellConsolidationState,
  draft: Draft,
  key: string
): void => {
  groupOf(state, key)?.members.forEach((member) =>
    draft.checkedIds.add(member.rowId)
  );
};

/**
 * Checking authors structure: the first check seeds Survivor 1, later checks fold into
 * the most recent survivor. A name only takes a role the FIRST time one of its records
 * is checked — checking a second record of a structured name just bumps its included
 * count.
 * @param {WellConsolidationState} state The current model.
 * @param {Draft} draft The working copy to settle.
 * @param {string} id The row id that was checked.
 * @return {WellConsolidationState} The next model.
 */
const applyCheck = (
  state: WellConsolidationState,
  draft: Draft,
  id: string
): WellConsolidationState => {
  const record = state.pool.find((well) => well.rowId === id);
  if (!record) return state;
  const key = nameKeyOf(record.wellName);
  draft.checkedIds.add(id);
  if (statusOf(state, key) !== 'unactioned') return commit(state, draft);

  if (draft.survivorKeys.length === 0) {
    draft.survivorKeys.push(key);
    draft.pins[key] = id;
  } else {
    draft.dependents.push({
      key,
      survivorKey: draft.survivorKeys[draft.survivorKeys.length - 1],
    });
  }
  draft.hiddenKeys.delete(key);
  return commit(state, draft);
};

/**
 * Unchecking only decrements. A name gives up its role only once none of its records
 * are included any more.
 * @param {WellConsolidationState} state The current model.
 * @param {Draft} draft The working copy to settle.
 * @param {string} id The row id that was unchecked.
 * @return {WellConsolidationState} The next model.
 */
const applyUncheck = (
  state: WellConsolidationState,
  draft: Draft,
  id: string
): WellConsolidationState => {
  const record = state.pool.find((well) => well.rowId === id);
  if (!record) return state;
  const key = nameKeyOf(record.wellName);
  draft.checkedIds.delete(id);
  const members = groupOf(state, key)?.members ?? [];
  const stillChecked = members.some((member) =>
    draft.checkedIds.has(member.rowId)
  );
  if (stillChecked) {
    // The name keeps its role, but if the record just unchecked was its pin, drop the stale pin so the
    // remaining checked records re-elect a survivor instead of the excluded one standing.
    if (draft.pins[key] === id) {
      draft.pins[key] =
        resolveSurvivingWell(members, draft.checkedIds, null)?.rowId ?? null;
    }
    return commit(state, draft);
  }

  const status = statusOf(state, key);
  if (status === 'survivor') tearDownSurvivor(state, draft, key);
  else if (status === 'dependent') {
    draft.dependents = draft.dependents.filter((dep) => dep.key !== key);
  }
  return commit(state, draft);
};

/**
 * Select-all replays single-click resolution over the whole pool at once: the standing
 * survivor keeps its pin, and every name without a role folds into the most recent
 * survivor.
 * @param {WellConsolidationState} state The current model.
 * @param {Draft} draft The working copy to settle.
 * @return {WellConsolidationState} The next model.
 */
const applyCheckAll = (
  state: WellConsolidationState,
  draft: Draft
): WellConsolidationState => {
  const anchor = state.pool.find((well) => well.isAnchor) ?? state.pool[0];
  if (!anchor) return state;
  const anchorKey = nameKeyOf(anchor.wellName);
  state.pool.forEach((well) => draft.checkedIds.add(well.rowId));
  if (draft.survivorKeys.length === 0) draft.survivorKeys = [anchorKey];
  // Survivor 1 keeps its pin; only an unpinned viewed-well survivor is re-seeded.
  const first = draft.survivorKeys[0];
  draft.pins[first] =
    draft.pins[first] ?? (first === anchorKey ? anchor.rowId : null);
  const newest = draft.survivorKeys[draft.survivorKeys.length - 1];
  groupsOf(state)
    .filter(
      (group) =>
        !draft.survivorKeys.includes(group.key) &&
        !draft.dependents.some((dep) => dep.key === group.key)
    )
    .forEach((group) =>
      draft.dependents.push({ key: group.key, survivorKey: newest })
    );
  draft.hiddenKeys.clear();
  return commit(state, draft);
};

/**
 * Toggling a name on elects it AND sweeps its records in, and lifts it out of any fold
 * it was sitting in. Toggling off is the same teardown as removal.
 * @param {WellConsolidationState} state The current model.
 * @param {Draft} draft The working copy to settle.
 * @param {string} key The name key that was toggled.
 * @return {WellConsolidationState} The next model.
 */
const applyToggleSurvivor = (
  state: WellConsolidationState,
  draft: Draft,
  key: string
): WellConsolidationState => {
  const group = groupOf(state, key);
  if (!group) return state;
  if (statusOf(state, key) === 'survivor') {
    tearDownSurvivor(state, draft, key);
    return commit(state, draft);
  }
  sweepChecked(state, draft, key);
  draft.survivorKeys.push(key);
  draft.dependents = draft.dependents.filter((dep) => dep.key !== key);
  draft.pins[key] =
    resolveSurvivingWell(group.members, draft.checkedIds, draft.pins[key])
      ?.rowId ?? group.members[0].rowId;
  draft.hiddenKeys.delete(key);
  return commit(state, draft);
};

/**
 * The Actions column. A survivor tears down, a folded name unfolds, and an unactioned
 * name is merely hidden so it can be restored.
 * @param {WellConsolidationState} state The current model.
 * @param {Draft} draft The working copy to settle.
 * @param {string} key The name key being removed.
 * @return {WellConsolidationState} The next model.
 */
const applyRemoveName = (
  state: WellConsolidationState,
  draft: Draft,
  key: string
): WellConsolidationState => {
  const status = statusOf(state, key);
  if (status === 'survivor') {
    tearDownSurvivor(state, draft, key);
  } else if (status === 'dependent') {
    groupOf(state, key)?.members.forEach((member) =>
      draft.checkedIds.delete(member.rowId)
    );
    draft.dependents = draft.dependents.filter((dep) => dep.key !== key);
  } else {
    draft.hiddenKeys.add(key);
  }
  return commit(state, draft);
};

/**
 * Dropping a name onto a survivor folds it in and sweeps its records. Only a
 * non-survivor may move, and only onto a survivor.
 * @param {WellConsolidationState} state The current model.
 * @param {Draft} draft The working copy to settle.
 * @param {string} key The dragged name key.
 * @param {string} survivorKey The survivor it was dropped on.
 * @return {WellConsolidationState} The next model.
 */
const applyDrag = (
  state: WellConsolidationState,
  draft: Draft,
  key: string,
  survivorKey: string
): WellConsolidationState => {
  const movable =
    key !== survivorKey &&
    state.survivorKeys.includes(survivorKey) &&
    !state.survivorKeys.includes(key) &&
    Boolean(groupOf(state, key));
  if (!movable) return state;

  draft.dependents = [
    ...draft.dependents.filter((dep) => dep.key !== key),
    { key, survivorKey },
  ];
  sweepChecked(state, draft, key);
  draft.hiddenKeys.delete(key);
  return commit(state, draft);
};

/**
 * Choosing the surviving record. When the chosen record belongs to a name folded
 * beneath this survivor, that name TAKES OVER as the survivor and the old one folds
 * under it — the structure follows the record the user picked.
 * @param {WellConsolidationState} state The current model.
 * @param {Draft} draft The working copy to settle.
 * @param {string} key The survivor's name key.
 * @param {string} id The row id chosen to survive.
 * @return {WellConsolidationState} The next model.
 */
const applySetRepresentative = (
  state: WellConsolidationState,
  draft: Draft,
  key: string,
  id: string
): WellConsolidationState => {
  const record = state.pool.find((well) => well.rowId === id);
  if (!record) return state;
  const newKey = nameKeyOf(record.wellName);
  if (newKey === key) {
    draft.pins[key] = id;
    return commit(state, draft);
  }
  draft.survivorKeys = draft.survivorKeys.map((survivorKey) =>
    survivorKey === key ? newKey : survivorKey
  );
  draft.dependents = [
    ...draft.dependents
      .filter((dep) => dep.key !== newKey)
      .map((dep) =>
        dep.survivorKey === key ? { ...dep, survivorKey: newKey } : dep
      ),
    { key, survivorKey: newKey },
  ];
  draft.pins[newKey] = id;
  draft.pins[key] = null;
  return commit(state, draft);
};

/**
 * The Reconcile Wells reducer. Each action settles a working copy of the mutable
 * slices, so one action can move several of them coherently.
 * @param {WellConsolidationState} state The current model.
 * @param {WellConsolidationAction} action What the user did.
 * @return {WellConsolidationState} The next model.
 */
export function wellConsolidationReducer(
  state: WellConsolidationState,
  action: WellConsolidationAction
): WellConsolidationState {
  const draft = draftOf(state);

  switch (action.type) {
    case 'check':
      return applyCheck(state, draft, action.id);

    case 'uncheck':
      return applyUncheck(state, draft, action.id);

    case 'checkAll':
      return applyCheckAll(state, draft);

    case 'uncheckAll':
      return {
        ...state,
        checkedIds: new Set(),
        survivorKeys: [],
        dependents: [],
        pins: {},
      };

    case 'toggleSurvivor':
      return applyToggleSurvivor(state, draft, action.key);

    // Include All only includes: a name's survivor/dependent role is untouched.
    case 'includeAll':
      sweepChecked(state, draft, action.key);
      return commit(state, draft);

    case 'removeName':
      return applyRemoveName(state, draft, action.key);

    case 'restoreName':
      draft.hiddenKeys.delete(action.key);
      return commit(state, draft);

    case 'drag':
      return applyDrag(state, draft, action.key, action.survivorKey);

    case 'setRepresentative':
      return applySetRepresentative(state, draft, action.key, action.id);

    // Wells pulled in from the catalog via "Add wells to reconcile". They join the pool (Current
    // Suggestions) but are NOT auto-included — the operator checks them into the reconcile by hand.
    // Already-present rows are ignored, so re-adding a shown well is a no-op.
    case 'addWells': {
      const present = new Set(state.pool.map((candidate) => candidate.rowId));
      const fresh = action.candidates.filter(
        (candidate) => !present.has(candidate.rowId)
      );
      if (fresh.length === 0) return state;
      return { ...state, pool: [...state.pool, ...fresh] };
    }

    default:
      return state;
  }
}

/**
 * Seed the model when the sheet opens: the viewed well is included on its own, its
 * distinct name is Survivor 1, and its record is pinned. Nothing else is touched, so
 * the sheet opens with Reconcile disabled until the user includes something.
 * @param {WellCandidate[]} pool The viewed well followed by its candidates.
 * @return {WellConsolidationState} The seeded model.
 */
export function initWellConsolidation(
  pool: WellCandidate[]
): WellConsolidationState {
  const anchor = pool.find((well) => well.isAnchor) ?? pool[0];
  if (!anchor) {
    return {
      pool,
      checkedIds: new Set(),
      survivorKeys: [],
      dependents: [],
      pins: {},
      hiddenKeys: new Set(),
    };
  }
  const anchorKey = nameKeyOf(anchor.wellName);
  return {
    pool,
    checkedIds: new Set([anchor.rowId]),
    survivorKeys: [anchorKey],
    dependents: [],
    pins: { [anchorKey]: anchor.rowId },
    hiddenKeys: new Set(),
  };
}
